import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBooking } from '../../contexts/BookingContext';
import { db, doc, updateDoc, serverTimestamp } from '../../firebase/config';
import PaymentModal from '../../components/PaymentModal';
import ReviewModal from '../../components/ReviewModal';
import ShareAsImageButton from '../../components/ShareAsImageButton';
import BookingShareCard from '../../components/BookingShareCard';
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, MapPin,
  User, X, Check, AlertCircle, CreditCard, MessageCircle, Star,
  CheckCircle2, XCircle, Loader2, ShoppingBag, Plus, Navigation,
  Car, Store,
} from 'lucide-react';

// ---------- Google Maps helpers ----------
const openDirections = ({ address, lat, lng }) => {
  let url;
  if (lat != null && lng != null) {
    url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  } else if (address) {
    url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  } else {
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

const CalendarPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { clientBookings, clientBookingsLoading } = useBooking();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [toast, setToast] = useState(null);
  const [payBooking, setPayBooking] = useState(null);
  const [reviewBooking, setReviewBooking] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  // ---------- Central total calculation ----------
  const calculateTotal = (booking) => {
    if (typeof booking.totalPrice === 'number' && booking.totalPrice > 0) {
      return booking.totalPrice;
    }
    const base =
      Number(booking.optionPrice) ||
      Number(booking.price) ||
      Number(booking.servicePrice) ||
      0;
    const addonsTotal = Array.isArray(booking.addons)
      ? booking.addons.reduce((sum, a) => sum + Number(a.price || 0), 0)
      : Number(booking.addonsTotal) || 0;
    const transport = Number(booking.transportFee) || 0;
    return base + addonsTotal + transport;
  };

  // ---------- Split bookings ----------
  const { upcoming, past } = useMemo(() => {
    const sorted = [...clientBookings].sort((a, b) => {
      const aT = new Date(`${a.date}T${a.time || '00:00'}`).getTime();
      const bT = new Date(`${b.date}T${b.time || '00:00'}`).getTime();
      return aT - bT;
    });
    const nowTs = now.getTime();
    const upcomingList = [];
    const pastList = [];
    sorted.forEach(b => {
      const dt = new Date(`${b.date}T${b.time || '00:00'}`).getTime();
      const isPast = dt < nowTs || b.status === 'completed' || b.status === 'cancelled';
      if (isPast) pastList.push(b);
      else upcomingList.push(b);
    });
    pastList.reverse();
    return { upcoming: upcomingList, past: pastList };
  }, [clientBookings, now]);

  // ---------- Calendar helpers ----------
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];
    for (let i = 0; i < firstDay; i++) days.push({ day: null, date: null });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const dayBookings = clientBookings.filter(b => {
        if (!b.date) return false;
        const bd = new Date(b.date);
        return (
          bd.getDate() === d &&
          bd.getMonth() === month &&
          bd.getFullYear() === year
        );
      });
      days.push({
        day: d,
        date,
        isToday: date.toDateString() === today.toDateString(),
        isPast: date < today,
        bookings: dayBookings,
      });
    }
    return days;
  };

  const days = getDaysInMonth();

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };
  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // ---------- Status helpers ----------
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Pending', icon: <Clock size={11} /> };
      case 'confirmed':
        return { bg: 'bg-green-50', text: 'text-green-600', label: 'Confirmed', icon: <Check size={11} /> };
      case 'in_progress':
        return { bg: 'bg-blue-50', text: 'text-blue-600', label: 'In Progress', icon: <Loader2 size={11} /> };
      case 'completed':
        return { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Completed', icon: <CheckCircle2 size={11} /> };
      case 'cancelled':
        return { bg: 'bg-red-50', text: 'text-red-500', label: 'Cancelled', icon: <XCircle size={11} /> };
      default:
        return { bg: 'bg-gray-50', text: 'text-gray-500', label: status || 'Unknown', icon: null };
    }
  };

  const getPaymentBadge = (paymentStatus, paymentPolicy) => {
    if (!paymentPolicy || paymentPolicy === 'after') return null;
    switch (paymentStatus) {
      case 'unpaid':
        return { bg: 'bg-[#FFF5F6]', text: 'text-[#EFA9B6]', label: 'Payment due' };
      case 'pending_transfer':
        return { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Awaiting confirmation' };
      case 'deposit_paid':
        return { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Deposit paid' };
      case 'paid_in_full':
        return { bg: 'bg-green-50', text: 'text-green-600', label: 'Paid in full' };
      case 'settled':
        return { bg: 'bg-green-50', text: 'text-green-600', label: 'Settled' };
      default:
        return null;
    }
  };

  const formatCountdown = (dateStr, timeStr) => {
    if (!dateStr) return '';
    const target = new Date(`${dateStr}T${timeStr || '00:00'}`).getTime();
    const diff = target - now.getTime();
    if (diff < 0) return 'Started';
    if (diff < 60000) return 'Starting now';
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `In ${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) {
      const rem = mins % 60;
      return rem > 0 ? `In ${hours}h ${rem}m` : `In ${hours}h`;
    }
    const days = Math.floor(hours / 24);
    return `In ${days} day${days > 1 ? 's' : ''}`;
  };

  const formatDateLong = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const formatPrice = (p) => `R${Number(p || 0).toFixed(2)}`;

  // ---------- Cancel booking ----------
  const handleCancel = async (bookingId) => {
    if (!window.confirm('Cancel this booking? This cannot be undone.')) return;
    setCancelling(true);
    try {
      await updateDoc(doc(db, 'bookings', bookingId), {
        status: 'cancelled',
        cancelledAt: serverTimestamp(),
        cancelledBy: 'client',
      });
      showToast('Booking cancelled');
      setSelectedBooking(null);
    } catch (err) {
      console.error('Cancel failed:', err);
      showToast('Failed to cancel', 'error');
    } finally {
      setCancelling(false);
    }
  };

  const handlePayNow = (booking) => setPayBooking(booking);
  const handleRateService = (booking) => setReviewBooking(booking);

  const handleMessagePro = (booking) => {
    if (!booking.professionalId) return;
    navigate(`/dashboard/chat?with=${booking.professionalId}`);
    setSelectedBooking(null);
  };

  // ============================================================
  // LOADING
  // ============================================================
  if (clientBookingsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#EFA9B6] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="max-w-6xl mx-auto">
      {/* Header — matches NotificationsPage / FavoritesPage */}

      {clientBookings.length === 0 ? (
        /* Empty state — matches NotificationsPage / FavoritesPage */
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
            <CalendarIcon size={32} className="text-[#EFA9B6]" />
          </div>
          <h3 className="text-lg font-serif font-bold text-gray-900 mb-2">
            No bookings yet
          </h3>
          <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
            Discover beauty professionals and book your first service
          </p>
          <button
            onClick={() => navigate('/dashboard/search')}
            className="px-6 py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors"
          >
            Discover Professionals
          </button>
        </div>
      ) : (
        <>
          {/* ============ CALENDAR GRID ============ */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-50 overflow-hidden mb-6">
            <div className="p-4 border-b border-gray-50 flex items-center justify-between">
              <h2 className="text-lg font-serif font-bold text-gray-900">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={prevMonth}
                  className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={nextMonth}
                  className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>

            <div className="p-4">
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                  <div key={i} className="text-center text-[10px] font-bold text-gray-400 uppercase py-1">
                    {d}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {days.map((day, idx) => {
                  const hasBookings = day.bookings?.length > 0;
                  const firstBooking = hasBookings ? day.bookings[0] : null;
                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        if (hasBookings) setSelectedBooking(firstBooking);
                      }}
                      disabled={!day.day}
                      className={`aspect-square rounded-xl flex flex-col items-center justify-center text-sm transition-all relative ${
                        !day.day
                          ? 'invisible'
                          : hasBookings
                          ? 'bg-[#FFF5F6] hover:bg-[#FBE1E8] cursor-pointer'
                          : day.isPast
                          ? 'text-gray-300'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className={`font-medium ${hasBookings ? 'text-[#EFA9B6] font-bold' : ''}`}>
                        {day.day}
                      </span>
                      {hasBookings && (
                        <div className="flex gap-0.5 mt-0.5">
                          {day.bookings.slice(0, 3).map((_, i) => (
                            <span key={i} className="w-1 h-1 rounded-full bg-[#EFA9B6]"></span>
                          ))}
                        </div>
                      )}
                      {day.isToday && !hasBookings && (
                        <span className="absolute inset-0 border-2 border-[#EFA9B6]/30 rounded-xl pointer-events-none"></span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ============ UPCOMING ============ */}
          {upcoming.length > 0 && (
            <div className="mb-6">
              <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3 ml-1">
                Upcoming
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {upcoming.map((booking) => (
                  <BookingCard
                    key={booking.id}
                    booking={booking}
                    now={now}
                    onClick={() => setSelectedBooking(booking)}
                    getStatusBadge={getStatusBadge}
                    getPaymentBadge={getPaymentBadge}
                    formatCountdown={formatCountdown}
                    calculateTotal={calculateTotal}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ============ PAST ============ */}
          {past.length > 0 && (
            <div>
              <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3 ml-1">
                Past
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {past.slice(0, 6).map((booking) => (
                  <BookingCard
                    key={booking.id}
                    booking={booking}
                    now={now}
                    onClick={() => setSelectedBooking(booking)}
                    getStatusBadge={getStatusBadge}
                    getPaymentBadge={getPaymentBadge}
                    formatCountdown={formatCountdown}
                    calculateTotal={calculateTotal}
                    isPast
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {selectedBooking && (
        <BookingDetailModal
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
          onCancel={handleCancel}
          onPay={handlePayNow}
          onMessage={handleMessagePro}
          onRate={handleRateService}
          cancelling={cancelling}
          getStatusBadge={getStatusBadge}
          getPaymentBadge={getPaymentBadge}
          formatDateLong={formatDateLong}
          formatCountdown={formatCountdown}
          calculateTotal={calculateTotal}
          formatPrice={formatPrice}
          now={now}
        />
      )}

      {payBooking && (
        <PaymentModal
          booking={payBooking}
          onClose={() => setPayBooking(null)}
          onSuccess={() => showToast('Payment received')}
        />
      )}

      {reviewBooking && (
        <ReviewModal
          booking={reviewBooking}
          onClose={() => setReviewBooking(null)}
          onSuccess={() => showToast('Thanks for your review!')}
        />
      )}

      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[110]">
          <div className={`px-5 py-3 rounded-full shadow-lg text-sm font-bold ${
            toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-[#010101] text-white'
          }`}>
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// BOOKING CARD — matches NotificationsPage / FavoritesPage card
// ============================================================
const BookingCard = ({
  booking, now, onClick, getStatusBadge, getPaymentBadge,
  formatCountdown, calculateTotal, isPast
}) => {
  const status = getStatusBadge(booking.status);
  const payment = getPaymentBadge(booking.paymentStatus, booking.paymentPolicy);
  const countdown = !isPast ? formatCountdown(booking.date, booking.time) : null;
  const total = calculateTotal(booking);

  const coverImage = booking.professionalImage ||
    (Array.isArray(booking.media) && booking.media[0]?.url) ||
    null;

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-2xl p-4 shadow-sm border border-gray-50 hover:shadow-md transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-12 h-12 rounded-xl bg-[#FFF5F6] flex items-center justify-center overflow-hidden shrink-0">
            {coverImage ? (
              <img src={coverImage} alt="" className="w-full h-full object-cover" />
            ) : (
              <User size={20} className="text-[#EFA9B6]" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-gray-900 truncate">
              {booking.serviceTitle || booking.serviceName || 'Service'}
            </h3>
            <p className="text-xs text-gray-500 truncate">
              {booking.professionalName || 'Professional'}
            </p>
            {countdown && !isPast && (
              <div className="flex items-center gap-1 mt-2">
                <Clock size={11} className="text-[#EFA9B6]" />
                <span className="text-[10px] font-bold text-[#EFA9B6]">
                  {countdown}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status + payment badges */}
      <div className="flex flex-wrap items-center gap-1.5 mt-3">
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${status.bg} ${status.text}`}>
          {status.icon}
          {status.label}
        </span>
        {payment && (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${payment.bg} ${payment.text}`}>
            {payment.label}
          </span>
        )}
      </div>

      {/* Date / time / price row */}
      <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-50">
        <div className="flex items-center gap-3 text-[11px] text-gray-500">
          <span className="flex items-center gap-1">
            <CalendarIcon size={11} />
            {new Date(booking.date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}
          </span>
          <span className="flex items-center gap-1">
            <Clock size={11} />
            {booking.time}
          </span>
        </div>
        <span className="font-bold text-gray-900 text-sm">
          R{total.toFixed(0)}
        </span>
      </div>

      {/* Action button — mirrors NotificationsPage / FavoritesPage */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className="w-full mt-3 py-2 bg-[#FFF5F6] text-[#EFA9B6] text-sm font-bold rounded-xl hover:bg-[#EFA9B6] hover:text-white transition-colors"
      >
        View details
      </button>
    </div>
  );
};

// ============================================================
// DETAIL MODAL — unchanged
// ============================================================
const BookingDetailModal = ({
  booking, onClose, onCancel, onPay, onMessage, onRate,
  cancelling, getStatusBadge, getPaymentBadge,
  formatDateLong, formatCountdown, calculateTotal, formatPrice, now
}) => {
  const status = getStatusBadge(booking.status);
  const payment = getPaymentBadge(booking.paymentStatus, booking.paymentPolicy);
  const countdown = formatCountdown(booking.date, booking.time);

  const canCancel = booking.status === 'pending' || booking.status === 'confirmed';
  const canPay =
    booking.paymentStatus === 'unpaid' || booking.paymentStatus === 'deposit_paid';
  const canRate = booking.status === 'completed' && !booking.rating;
  const alreadyRated = booking.status === 'completed' && booking.rating;
  const canMessage = booking.professionalId && booking.status !== 'cancelled';

  const total = calculateTotal(booking);
  const basePrice =
    Number(booking.optionPrice) ||
    Number(booking.price) ||
    Number(booking.servicePrice) ||
    0;
  const hasAddons = Array.isArray(booking.addons) && booking.addons.length > 0;
  const hasTransport = Number(booking.transportFee || 0) > 0;

  const deposit = Number(booking.depositAmount || 0);
  const paid = Number(booking.amountPaid || 0);
  const due = booking.amountDue ?? (total - paid);

  const isMobile = booking.locationMode === 'mobile';
  const isSalon = booking.locationMode === 'salon';

  const displayAddress = isMobile
    ? (booking.serviceAddress || booking.location || '')
    : (booking.salonAddress || booking.location || '');

  const canGetDirections = isSalon && !!(booking.salonAddress || booking.location);
  const directionsTarget = isSalon
    ? (booking.salonAddress || booking.location)
    : null;

  const proCoords = booking.proCoordinates || null;

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60" onClick={onClose}>
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-gray-50 flex items-start justify-between shrink-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${status.bg} ${status.text}`}>
                {status.icon}
                {status.label}
              </span>
              {payment && (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${payment.bg} ${payment.text}`}>
                  {payment.label}
                </span>
              )}
            </div>
            <h2 className="font-serif font-bold text-xl text-gray-900 truncate">
              {booking.serviceTitle || booking.serviceName || 'Service'}
            </h2>
            <p className="text-sm text-gray-500 truncate">
              {booking.professionalName || 'Professional'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center hover:bg-gray-100 shrink-0 ml-2"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
          {(booking.status === 'confirmed' || booking.status === 'pending') && (
            <div className="bg-[#FFF5F6] rounded-2xl p-4 text-center">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-1">
                Appointment
              </p>
              <p className="font-bold text-gray-900 text-lg">{countdown}</p>
            </div>
          )}

          <div className="bg-[#FCF8F9] rounded-2xl p-4 space-y-3">
            <InfoRow icon={<CalendarIcon size={16} />} label="Date" value={formatDateLong(booking.date)} />
            <InfoRow icon={<Clock size={16} />} label="Time" value={booking.time || '—'} />
            {booking.duration && (
              <InfoRow icon={<Clock size={16} />} label="Duration" value={booking.duration} />
            )}
          </div>

          <div className="bg-white border border-gray-100 rounded-2xl p-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#FFF5F6] flex items-center justify-center shrink-0 text-[#EFA9B6]">
                {isMobile ? <Car size={18} /> : <Store size={18} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-0.5">
                  {isMobile ? 'Mobile service' : 'Salon visit'}
                </p>
                <p className="font-bold text-gray-900 text-sm">
                  {isMobile
                    ? `${booking.professionalName || 'The pro'} comes to you`
                    : 'You go to the professional'}
                </p>
                {displayAddress && (
                  <p className="text-xs text-gray-600 mt-1 break-words">
                    {displayAddress}
                  </p>
                )}
                {!displayAddress && (
                  <p className="text-xs text-gray-400 mt-1 italic">
                    No address on file — message the pro to confirm
                  </p>
                )}
              </div>
            </div>

            {canGetDirections && (
              <button
                type="button"
                onClick={() => openDirections({
                  address: directionsTarget,
                  lat: proCoords?.lat,
                  lng: proCoords?.lng,
                })}
                className="w-full py-2.5 rounded-xl bg-[#010101] text-white font-bold text-xs hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
              >
                <Navigation size={14} />
                Get directions
              </button>
            )}

            {isMobile && (
              <div className="mt-2 p-3 bg-[#FCF8F9] rounded-xl">
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  <span className="font-bold text-gray-700">Heads up:</span>{' '}
                  {booking.professionalName?.split(' ')[0] || 'The pro'} will travel to your address. Make sure it's easy to find and you're home at the appointment time.
                  {booking.transportFee > 0 && (
                    <> A transport fee of {formatPrice(booking.transportFee)} is included in the total.</>
                  )}
                </p>
              </div>
            )}
          </div>

          <div className="bg-[#FCF8F9] rounded-2xl p-4">
            <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
              <CreditCard size={12} className="text-[#EFA9B6]" />
              Breakdown
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">
                  {booking.optionName || 'Service price'}
                </span>
                <span className="font-bold text-gray-900">{formatPrice(basePrice)}</span>
              </div>

              {hasAddons && booking.addons.map((addon, idx) => (
                <div key={addon.id || idx} className="flex justify-between">
                  <span className="text-gray-500 flex items-center gap-1.5">
                    <Plus size={11} className="text-[#AF8BD4]" />
                    {addon.name}
                  </span>
                  <span className="font-bold text-[#AF8BD4]">
                    +{formatPrice(addon.price)}
                  </span>
                </div>
              ))}

              {hasTransport && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Transport fee</span>
                  <span className="font-bold text-[#AF8BD4]">
                    +{formatPrice(booking.transportFee)}
                  </span>
                </div>
              )}

              <div className="flex justify-between pt-2 border-t border-gray-100">
                <span className="font-bold text-gray-900">Total</span>
                <span className="font-bold text-gray-900">{formatPrice(total)}</span>
              </div>
            </div>
          </div>

          {booking.paymentPolicy && booking.paymentPolicy !== 'after' && (
            <div className="bg-[#FCF8F9] rounded-2xl p-4">
              <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
                <CreditCard size={12} className="text-[#EFA9B6]" />
                Payment
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Paid</span>
                  <span className="font-bold text-green-600">{formatPrice(paid)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-100">
                  <span className="font-bold text-gray-900">Due</span>
                  <span className="font-bold text-[#EFA9B6]">{formatPrice(due)}</span>
                </div>
              </div>
            </div>
          )}

          {booking.paymentPolicy === 'after' && (
            <div className="bg-[#FCF8F9] rounded-2xl p-4">
              <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-2 flex items-center gap-2">
                <CreditCard size={12} className="text-[#EFA9B6]" />
                Payment
              </h3>
              <p className="text-sm text-gray-600">
                Pay <span className="font-bold text-gray-900">{formatPrice(total)}</span> at your appointment.
              </p>
            </div>
          )}

          {booking.paymentStatus === 'pending_transfer' && (
            <div className="bg-amber-50 rounded-2xl p-4 flex items-start gap-2">
              <AlertCircle size={16} className="text-amber-500 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-700">
                <p className="font-bold">Waiting for confirmation</p>
                <p className="mt-0.5">
                  {booking.professionalName} will confirm once they receive your payment.
                </p>
              </div>
            </div>
          )}

          {alreadyRated && (
            <div className="bg-[#FFF5F6] rounded-2xl p-4">
              <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-2 flex items-center gap-2">
                <Star size={12} className="text-[#EFA9B6]" />
                Your Rating
              </h3>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    size={16}
                    className={n <= booking.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
                  />
                ))}
                <span className="ml-2 font-bold text-gray-900">{booking.rating}/5</span>
              </div>
            </div>
          )}

          {booking.notes && (
            <div className="bg-[#FCF8F9] rounded-2xl p-4">
              <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] mb-2">
                Your Notes
              </h3>
              <p className="text-sm text-gray-600 italic">"{booking.notes}"</p>
            </div>
          )}
        </div>

        <div className="p-5 pt-4 border-t border-gray-50 shrink-0 space-y-2">
          <ShareAsImageButton
            filename={`skytone-booking-${booking.id || Date.now()}`}
            title="My Skytone booking"
            text={`I booked ${booking.serviceTitle || booking.serviceName || 'a service'} on Skytone`}
            label="Share as image"
            className="w-full py-3 rounded-full bg-[#FFF5F6] text-[#EFA9B6] text-sm hover:bg-[#FBE1E8]"
          >
            <BookingShareCard booking={booking} calculateTotal={calculateTotal} />
          </ShareAsImageButton>

          {canPay && (
            <button
              onClick={() => {
                const b = booking;
                onClose();
                onPay(b);
              }}
              className="w-full py-3.5 rounded-full bg-[#010101] text-white font-bold text-sm hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
            >
              <CreditCard size={16} />
              {booking.paymentStatus === 'unpaid'
                ? `Pay R${(deposit > 0 ? deposit : total).toFixed(2)}`
                : `Pay Remaining R${due.toFixed(2)}`}
            </button>
          )}

          {canRate && (
            <button
              onClick={() => {
                const b = booking;
                onClose();
                onRate(b);
              }}
              className="w-full py-3.5 rounded-full bg-[#010101] text-white font-bold text-sm hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
            >
              <Star size={16} />
              Rate this service
            </button>
          )}

          <div className="flex gap-2">
            {canMessage && (
              <button
                onClick={() => onMessage(booking)}
                className="flex-1 py-3 rounded-full bg-[#FCF8F9] text-gray-700 font-bold text-sm hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle size={16} />
                Message
              </button>
            )}
            {canCancel && (
              <button
                onClick={() => onCancel(booking.id)}
                disabled={cancelling}
                className="flex-1 py-3 rounded-full bg-red-50 text-red-500 font-bold text-sm hover:bg-red-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {cancelling ? (
                  <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  'Cancel'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="text-[#EFA9B6] mt-0.5 shrink-0">{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em]">{label}</p>
      <p className="text-sm text-gray-900 font-medium truncate">{value}</p>
    </div>
  </div>
);

export default CalendarPage;